import {
	Box,
	Icons,
	Container,
	Inline,
	Heading,
	Col,
	Grid,
	Stack,
	Button,
	Text,
	Time,
	MoneySum,
	Divider,
	Badge,
} from "@sembark-travel/ui-base"
import { TTouristProfile } from "./store"
import {
	parseDate,
	utcTimestampToLocalDateString,
} from "@sembark-travel/datetime-utils"
import { EditTouristProfileDialog } from "./EditItem"
import { Email, PhoneNumber } from "../Contacts"

export function TouristProfileDetails({
	touristProfile,
	onChange,
}: {
	touristProfile: TTouristProfile
	onChange: (touristProfileId: number) => void
}) {
	return (
		<Container bgColor="default" paddingY="6">
			<Stack gap="6">
				<ProfileHeader touristProfile={touristProfile} onChange={onChange} />
				<Statistics touristProfile={touristProfile} />
				<ContactInformation touristProfile={touristProfile} />
				<AdditionalInformation touristProfile={touristProfile} />
			</Stack>
		</Container>
	)
}

function ProfileHeader({
	touristProfile,
	onChange,
}: {
	touristProfile: TTouristProfile
	onChange: (touristProfileId: number) => void
}) {
	const { id, name, name_without_salutation, dob, nationality, tags } =
		touristProfile

	return (
		<Inline gap="6" justifyContent="between" flexWrap="wrap">
			<Inline gap="4" flex="1">
				<Stack>
					<Inline
						size="16"
						alignItems="center"
						justifyContent="center"
						rounded="full"
						boxShadow="base"
						bgColor="primary"
						fontSize="lg"
						fontWeight="semibold"
						borderWidth="1"
						color="primary"
						letterSpacing="wider"
					>
						{name_without_salutation?.slice(0, 2)}
					</Inline>
				</Stack>
				<Stack gap="2">
					<Heading as="h2">{name}</Heading>
					<Inline gap="4" flexWrap="wrap">
						<Stack gap="1">
							<Text as="span" color="muted">
								Date Of Birth
							</Text>
							<Text as="span" fontWeight="semibold">
								{dob ? (
									<Time value={parseDate(dob)} />
								) : (
									<Text color="muted">N/A</Text>
								)}
							</Text>
						</Stack>
						<Stack gap="1">
							<Text as="span" color="muted">
								Nationality
							</Text>
							<Text as="span" fontWeight="semibold">
								{typeof nationality === "string"
									? nationality
									: nationality?.nationality || <Text color="muted">N/A</Text>}
							</Text>
						</Stack>
						<Stack gap="1">
							<Text as="span" color="muted">
								Tags
							</Text>
							<Text as="span" fontWeight="semibold">
								{tags && tags.length ? (
									<Inline gap="1">
										{tags.map((t) => (
											<Badge
												key={t.id}
												title={
													t.pivot?.created_at
														? utcTimestampToLocalDateString(t.pivot.created_at)
														: undefined
												}
											>
												{t.name}
											</Badge>
										))}
									</Inline>
								) : (
									<Box
										color="muted"
										fontSize="sm"
										display="inline"
										title={`Tag trips to quickly indentify and group trips`}
									>
										No Tags
									</Box>
								)}
							</Text>
						</Stack>
					</Inline>
				</Stack>
			</Inline>
			<Inline gap="4">
				<EditTouristProfileDialog
					touristProfile={touristProfile}
					onSuccess={() => onChange(id)}
				>
					{({ show }) => (
						<Button onClick={() => show()} size="sm">
							<Icons.Pencil /> Edit Details
						</Button>
					)}
				</EditTouristProfileDialog>
			</Inline>
		</Inline>
	)
}

function Statistics({ touristProfile }: { touristProfile: TTouristProfile }) {
	const { total_queries, total_converted_queries, total_revenue } =
		touristProfile

	return (
		<Grid>
			<Col sm={12} md={4} padding="2">
				<Stack bgColor="subtle" padding="3" rounded="lg">
					<Text as="span" color="muted">
						Total Queries
					</Text>
					<Text as="span" fontWeight="semibold">
						{total_queries ? total_queries : <Text color="muted">N/A</Text>}
					</Text>
				</Stack>
			</Col>
			<Col sm={12} md={4} padding="2">
				<Stack bgColor="subtle" padding="3" rounded="lg">
					<Text as="span" color="muted">
						Converted Trips
					</Text>
					<Text as="span" fontWeight="semibold">
						{total_converted_queries ? (
							total_converted_queries
						) : (
							<Text color="muted">N/A</Text>
						)}
					</Text>
				</Stack>
			</Col>
			<Col sm={12} md={4} padding="2">
				<Stack bgColor="subtle" padding="3" rounded="lg">
					<Text as="span" color="muted">
						Total Revenue
					</Text>
					<Text as="span" fontWeight="semibold">
						{total_revenue?.length > 0 ? (
							<MoneySum money={total_revenue} roundOff />
						) : (
							<Text color="muted">N/A</Text>
						)}
					</Text>
				</Stack>
			</Col>
		</Grid>
	)
}

function ContactInformation({
	touristProfile,
}: {
	touristProfile: TTouristProfile
}) {
	const { address, phone_numbers, emails } = touristProfile

	return (
		<Stack gap="2">
			<Heading fontSize="md">Contact Information</Heading>
			<Box
				borderWidth="1"
				borderColor="muted"
				rounded="lg"
				padding="2"
				paddingX="3"
				gap="4"
			>
				<Stack flexWrap="wrap" gap="4">
					{address && (
						<Inline gap="2" alignItems="center">
							<Icons.LocationMarker />
							<Text>{address.location.full_name}</Text>
						</Inline>
					)}
					<Inline gap="4">
						<Box>
							<PhoneNumber value={phone_numbers} />
						</Box>
						<Box>
							<Email value={emails?.find((e) => e?.is_primary)} />
						</Box>
					</Inline>
				</Stack>
			</Box>
		</Stack>
	)
}

function AdditionalInformation({
	touristProfile,
}: {
	touristProfile: TTouristProfile
}) {
	const {
		meal_preference,
		stay_preference,
		language_preference,
		preferred_airport,
		preferred_flight_class,
		marriage_anniversary,
		is_smoker,
		passport_number,
		passport_expiry_date,
		company_name,
		gst_number,
		remarks,
	} = touristProfile

	return (
		<Stack gap="2">
			<Heading fontSize="md">Additional Information</Heading>
			<Box
				borderWidth="1"
				borderColor="muted"
				rounded="lg"
				paddingY="2"
				paddingX="3"
			>
				<Stack gap="4">
					<Text as="span" color="muted">
						Travel Preferences
					</Text>
					<Inline gap="8" flexWrap="wrap">
						<Stack gap="3">
							<Text as="sub" color="muted">
								Meal Preference
							</Text>
							<Text as="span" fontWeight="semibold">
								{meal_preference ? (
									meal_preference
								) : (
									<Text color="muted">N/A</Text>
								)}
							</Text>
						</Stack>
						<Stack gap="3">
							<Text as="sub" color="muted">
								Stay Preference
							</Text>
							<Text as="span" fontWeight="semibold">
								{stay_preference ? (
									stay_preference
								) : (
									<Text color="muted">N/A</Text>
								)}
							</Text>
						</Stack>
						<Stack gap="3">
							<Text as="sub" color="muted">
								Language Preference
							</Text>
							<Text as="span" fontWeight="semibold">
								{language_preference ? (
									language_preference
								) : (
									<Text color="muted">N/A</Text>
								)}
							</Text>
						</Stack>
						<Stack gap="3">
							<Text as="sub" color="muted">
								Preferred Airport
							</Text>
							<Text as="span" fontWeight="semibold">
								{preferred_airport ? (
									preferred_airport?.name
								) : (
									<Text color="muted">N/A</Text>
								)}
							</Text>
						</Stack>
						<Stack gap="3">
							<Text as="sub" color="muted">
								Flight Class Preference
							</Text>
							<Text as="span" fontWeight="semibold">
								{preferred_flight_class ? (
									preferred_flight_class
								) : (
									<Text color="muted">N/A</Text>
								)}
							</Text>
						</Stack>
					</Inline>
				</Stack>
				<Divider sm />
				<Stack gap="4">
					<Text as="span" color="muted">
						Other Preferences
					</Text>
					<Inline gap="8" flexWrap="wrap">
						<Stack gap="3">
							<Text as="sub" color="muted">
								Marriage Anniversary
							</Text>
							<Text as="span" fontWeight="semibold">
								{marriage_anniversary ? (
									<Time timestamp={marriage_anniversary} />
								) : (
									<Text color="muted">N/A</Text>
								)}
							</Text>
						</Stack>
						<Stack gap="3">
							<Text as="sub" color="muted">
								Smoking
							</Text>
							<Text as="span" fontWeight="semibold">
								{is_smoker === null ? (
									<Text color="muted">N/A</Text>
								) : is_smoker ? (
									"Yes"
								) : (
									"No"
								)}
							</Text>
						</Stack>
						<Stack gap="3">
							<Text as="sub" color="muted">
								Passport Number
							</Text>
							<Text as="span" fontWeight="semibold">
								{passport_number ? (
									passport_number
								) : (
									<Text color="muted">N/A</Text>
								)}
							</Text>
						</Stack>
						<Stack gap="3">
							<Text as="sub" color="muted">
								Passport Expiry Date
							</Text>
							<Text as="span" fontWeight="semibold">
								{passport_expiry_date ? (
									<Time value={parseDate(passport_expiry_date)} />
								) : (
									<Text color="muted">N/A</Text>
								)}
							</Text>
						</Stack>
					</Inline>
				</Stack>
				<Divider sm />
				<Stack gap="4">
					<Text as="span" color="muted">
						Billing & Business details
					</Text>
					<Inline gap="8" flexWrap="wrap">
						<Stack gap="3">
							<Text as="sub" color="muted">
								Company Name
							</Text>
							<Text as="span" fontWeight="semibold">
								{company_name ? company_name : <Text color="muted">N/A</Text>}
							</Text>
						</Stack>
						<Stack gap="3">
							<Text as="sub" color="muted">
								Tax Details
							</Text>
							<Text as="span" fontWeight="semibold">
								{gst_number ? gst_number : <Text color="muted">N/A</Text>}
							</Text>
						</Stack>
						<Stack gap="3">
							<Text as="sub" color="muted">
								Remarks
							</Text>
							<Text as="span" fontWeight="semibold">
								{remarks ? remarks : <Text color="muted">N/A</Text>}
							</Text>
						</Stack>
					</Inline>
				</Stack>
			</Box>
		</Stack>
	)
}
